/**
 * Favorites Grid Component
 * Grid view for favorites with filtering tabs and responsive layout
 */



import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Store, ShoppingBag, X, Filter } from 'lucide-react';
import { useFavorites } from '@/hooks/use-favorites';
import { FavoriteItem, FavoriteType } from '@/types/favorites';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FavoritesButton } from './favorites-button';
import { toast } from 'sonner';

const typeIcons: Record<FavoriteType, React.ReactNode> = {
  market: <MapPin className="h-4 w-4" />,
  vendor: <Store className="h-4 w-4" />,
  product: <ShoppingBag className="h-4 w-4" />,
};

const typeLabels: Record<FavoriteType, string> = {
  market: 'Markets',
  vendor: 'Vendors',
  product: 'Products',
};

const typeColors: Record<FavoriteType, string> = {
  market: 'bg-sage text-bark dark:bg-forest/40 dark:text-cream',
  vendor: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  product: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
};

const typeHref: Record<FavoriteType, string> = {
  market: '/markets',
  vendor: '/vendors',
  product: '/produce',
};

interface FavoritesGridProps {
  className?: string;
}

export function FavoritesGrid({ className }: FavoritesGridProps) {
  const { favorites, removeFavorite, marketCount, vendorCount, productCount } = useFavorites();
  const [activeTab, setActiveTab] = useState<FavoriteType | 'all'>('all');

  const filteredFavorites = useMemo(() => {
    if (activeTab === 'all') {
      return favorites;
    }
    return favorites.filter((item) => item.type === activeTab);
  }, [favorites, activeTab]);

  const handleRemove = useCallback(
    (id: string, type: FavoriteType, name: string) => {
      const success = removeFavorite(id, type);
      if (success) {
        toast.success(`${name} removed from favorites`);
      }
    },
    [removeFavorite]
  );

  const hasFavorites = favorites.length > 0;

  return (
    <div className={className}>
      {hasFavorites ? (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as FavoriteType | 'all')}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <TabsList className="grid w-full sm:w-auto grid-cols-4">
              <TabsTrigger value="all" className="relative">
                All
                <span className="ml-1.5 text-xs text-muted-foreground">
                  ({favorites.length})
                </span>
              </TabsTrigger>
              <TabsTrigger value="market">
                Markets
                <span className="ml-1.5 text-xs text-muted-foreground">
                  ({marketCount})
                </span>
              </TabsTrigger>
              <TabsTrigger value="vendor">
                Vendors
                <span className="ml-1.5 text-xs text-muted-foreground">
                  ({vendorCount})
                </span>
              </TabsTrigger>
              <TabsTrigger value="product">
                Products
                <span className="ml-1.5 text-xs text-muted-foreground">
                  ({productCount})
                </span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value={activeTab} className="mt-0">
            {filteredFavorites.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredFavorites.map((item) => (
                  <FavoriteCard
                    key={`${item.type}:${item.id}`}
                    item={item}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Filter className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No favorites in this category</h3>
                <p className="text-muted-foreground">
                  Try switching to a different tab to see more favorites.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      ) : (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
            <Store className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">No favorites yet</h3>
          <p className="text-muted-foreground mb-4 max-w-md mx-auto">
            Start exploring markets, vendors, and products to save your favorites here.
          </p>
          <div className="flex gap-2 justify-center">
            <Button asChild>
              <Link to="/markets">Browse Markets</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/vendors">Browse Vendors</Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface FavoriteCardProps {
  item: FavoriteItem;
  onRemove: (id: string, type: FavoriteType, name: string) => void;
}

function FavoriteCard({ item, onRemove }: FavoriteCardProps) {
  const typeColor = typeColors[item.type];

  return (
    <Card className="group overflow-hidden transition-all hover:shadow-md">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${typeColor}`}>
            {typeIcons[item.type]}
            {typeLabels[item.type]}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 -mr-1 -mt-1 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => onRemove(item.id, item.type, item.name)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <Link to={`${typeHref[item.type]}/${item.slug}`} className="block group-hover:text-primary transition-colors">
          <h3 className="font-semibold line-clamp-1 mb-1">{item.name}</h3>
          {item.metadata?.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {item.metadata.description}
            </p>
          )}
          {item.metadata?.location && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3 flex-shrink-0" />
              <span className="line-clamp-1">{item.metadata.location}</span>
            </p>
          )}
          {item.type === 'product' && item.metadata?.category && (
            <p className="text-xs text-muted-foreground mt-1">
              Category: {item.metadata.category}
            </p>
          )}
        </Link>
      </CardContent>

      <CardFooter className="p-4 pt-0 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          Added {formatDate(item.addedAt)}
        </span>
        <FavoritesButton
          id={item.id}
          type={item.type}
          name={item.name}
          slug={item.slug}
          variant="ghost"
          size="sm"
        />
      </CardFooter>
    </Card>
  );
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'today';
  } else if (diffDays === 1) {
    return 'yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }
}
